//
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';

// interface CanvasProps {
//   color: string,
// }

// type Coordinate = {
//   x: number;
//   y: number;
// };

// eslint-disable-next-line react/display-name, react/prop-types
const Canvas = forwardRef(({ color, ...other }, ref) => {
  const canvasRef = useRef();

  const [isPainting, setIsPainting] = useState(false);
  const [mousePosition, setMousePosition] = useState(undefined);

  useImperativeHandle(ref, () => ({
    clear() {
      const canvas = canvasRef.current;

      let width = { canvas };
      width += 0;

      canvas.width = width;
    },
  }));

  const startPaint = useCallback((event) => {
    const coordinates = getCoordinates(event);
    if (coordinates) {
      setMousePosition(coordinates);
      setIsPainting(true);
    }
  }, []);

  useEffect(() => {
    if (!canvasRef.current) {
      return undefined;
    }
    const canvas = canvasRef.current;
    canvas.addEventListener('mousedown', startPaint);
    return () => {
      canvas.removeEventListener('mousedown', startPaint);
    };
  }, [canvasRef, startPaint]);

  const drawLine = useCallback(
    (originalMousePosition, newMousePosition) => {
      if (!canvasRef.current) {
        return;
      }
      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');
      if (context) {
        context.strokeStyle = color;
        context.lineJoin = 'round';
        context.lineWidth = 5;

        context.beginPath();
        context.moveTo(originalMousePosition.x, originalMousePosition.y);
        context.lineTo(newMousePosition.x, newMousePosition.y);
        context.closePath();

        context.stroke();
      }
    },
    [canvasRef, color],
  );

  const paint = useCallback(
    (event) => {
      if (isPainting) {
        const newMousePosition = getCoordinates(event);
        if (mousePosition && newMousePosition) {
          drawLine(mousePosition, newMousePosition);
          setMousePosition(newMousePosition);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [drawLine, isPainting, mousePosition],
  );

  useEffect(() => {
    if (!canvasRef.current) {
      return undefined;
    }
    const canvas = canvasRef.current;
    canvas.addEventListener('mousemove', paint);

    const width = canvas.clientWidth;
    const height = canvas.clientHeight;

    if (canvas.width !== width || canvas.height !== height) {
      canvas.width = width;
      canvas.height = height;
    }

    return () => {
      canvas.removeEventListener('mousemove', paint);
    };
  }, [canvasRef, paint]);

  const exitPaint = useCallback(() => {
    setIsPainting(false);
    setMousePosition(undefined);
  }, []);

  useEffect(() => {
    if (!canvasRef.current) {
      return undefined;
    }
    document.addEventListener('mouseup', exitPaint);
    document.addEventListener('mouseleave', exitPaint);
    return () => {
      document.removeEventListener('mouseup', exitPaint);
      document.removeEventListener('mouseleave', exitPaint);
    };
  }, [exitPaint]);

  const getCoordinates = (event) => {
    if (!canvasRef.current) {
      return undefined;
    }

    const canvas = canvasRef.current;
    return { x: event.pageX - canvas.offsetLeft, y: event.pageY - canvas.offsetTop };
  };

  return <canvas ref={canvasRef} {...other} />;
});

export default Canvas;
